/**
 * Sortable + resizable table-column helpers, shared by Realtors + Outreach.
 *
 * Usage:
 *
 *   const cols = useResizableColumns("realtors", [
 *     { key: "name", label: "Name", width: 280, sortable: true },
 *     { key: "brokerage", label: "Brokerage", width: 200, sortable: true },
 *     ...
 *   ]);
 *
 *   <table>
 *     <colgroup>{cols.colGroup}</colgroup>
 *     <thead>
 *       <tr>
 *         <SortableTH col={cols.by("name")} sort={sort} onSort={setSort}>
 *           Name
 *         </SortableTH>
 *         ...
 *       </tr>
 *     </thead>
 *   </table>
 *
 * Widths persist to localStorage per storageKey. Drag the right edge of any
 * header to resize; click the header text (not the resizer) to toggle sort.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";

// ------------------------- Sort state helpers --------------------------

/**
 * Parses a compact "col:dir" sort string (e.g. "name:asc") into a
 * { column, direction } object. Empty / null → { column: null, direction: "asc" }.
 */
export function parseSort(sortStr) {
  if (!sortStr || typeof sortStr !== "string") return { column: null, direction: "asc" };
  const [col, dir] = sortStr.split(":");
  return { column: col || null, direction: dir === "desc" ? "desc" : "asc" };
}

export function stringifySort({ column, direction }) {
  if (!column) return "";
  return `${column}:${direction === "desc" ? "desc" : "asc"}`;
}

/**
 * Given the current sort string + a column key that was just clicked,
 * return the next sort string. Three-state cycle:
 *   nothing → asc → desc → nothing.
 */
export function nextSort(currentSort, columnKey) {
  const { column, direction } = parseSort(currentSort);
  if (column !== columnKey) return `${columnKey}:asc`;
  if (direction === "asc") return `${columnKey}:desc`;
  return ""; // desc → clear
}

// ---------------------- Resizable columns hook ------------------------

const MIN_COL_WIDTH = 60;
const MAX_COL_WIDTH = 900;

/**
 * Manages a set of columns' widths, persisting them to localStorage under
 * `storageKey` so a user's layout survives page reloads. Returns:
 *   - defs: the original column defs (mutated with .width from storage)
 *   - by(key): lookup a column def by key
 *   - colGroup: JSX <col> elements for the table's <colgroup>
 *   - beginDrag(key, e): pointerdown handler for the resize handle
 *   - reset(): restore defaults + purge storage
 */
export function useResizableColumns(storageKey, defaultDefs) {
  // Snapshot the defaults exactly once so we can restore them via reset().
  const defaults = useRef(defaultDefs.map((d) => ({ ...d })));
  const [widths, setWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(`col-widths:${storageKey}`);
      if (!saved) return Object.fromEntries(defaultDefs.map((d) => [d.key, d.width]));
      const parsed = JSON.parse(saved) || {};
      return Object.fromEntries(
        defaultDefs.map((d) => [d.key, Number(parsed[d.key]) || d.width]),
      );
    } catch (_) {
      return Object.fromEntries(defaultDefs.map((d) => [d.key, d.width]));
    }
  });

  // Persist on every change (debounced through natural React batching).
  useEffect(() => {
    try {
      localStorage.setItem(`col-widths:${storageKey}`, JSON.stringify(widths));
    } catch (_) { /* quota / privacy — best-effort */ }
  }, [widths, storageKey]);

  const defs = useMemo(
    () => defaultDefs.map((d) => ({ ...d, width: widths[d.key] ?? d.width })),
    [defaultDefs, widths],
  );

  const by = useCallback((key) => defs.find((d) => d.key === key) || null, [defs]);

  const colGroup = defs.map((d) => (
    <col key={d.key} style={{ width: `${d.width}px`, minWidth: `${d.width}px` }} />
  ));

  // Pointer-based drag — works with mouse + touch + trackpad. We capture
  // the pointer on the handle so the drag continues even if the cursor
  // slides off the (narrow) grabber during a fast move.
  const dragState = useRef(null);
  const beginDrag = useCallback((key, e) => {
    e.preventDefault();
    e.stopPropagation();
    const target = e.currentTarget;
    try { target.setPointerCapture(e.pointerId); } catch (_) { /* older browsers */ }
    dragState.current = {
      key,
      startX: e.clientX,
      startWidth: widths[key] ?? defaultDefs.find((d) => d.key === key)?.width ?? 120,
      pointerId: e.pointerId,
      handleEl: target,
    };

    const onMove = (ev) => {
      const st = dragState.current;
      if (!st) return;
      const dx = ev.clientX - st.startX;
      const next = Math.max(MIN_COL_WIDTH, Math.min(MAX_COL_WIDTH, st.startWidth + dx));
      setWidths((prev) => ({ ...prev, [st.key]: next }));
    };
    const onUp = (ev) => {
      const st = dragState.current;
      if (st?.handleEl) {
        try { st.handleEl.releasePointerCapture(ev.pointerId); } catch (_) { /* noop */ }
      }
      dragState.current = null;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }, [widths, defaultDefs]);

  const reset = useCallback(() => {
    const initial = Object.fromEntries(defaults.current.map((d) => [d.key, d.width]));
    setWidths(initial);
    try { localStorage.removeItem(`col-widths:${storageKey}`); } catch (_) { /* noop */ }
  }, [storageKey]);

  return { defs, by, colGroup, beginDrag, reset };
}

// ---------------------- SortableTH component --------------------------

/**
 * Table header cell that:
 *  - Renders a click-to-sort chevron on the right when col.sortable
 *  - Renders a 4px drag handle on the far right when col.resizable !== false
 *  - Uses fixed width from col.width so <colgroup> and <th> stay in sync
 *
 * Uses inline SVG + Tailwind so it works with the existing shadcn table
 * primitives without any extra dependencies.
 */
export function SortableTH({
  col,
  sort,
  onSort,
  onBeginResize,
  className = "",
  align = "left",
  testId,
  children,
}) {
  const { column, direction } = parseSort(sort);
  const isActive = column === col.key;
  const alignCls = align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";
  const sortable = col.sortable !== false;
  const resizable = col.resizable !== false;

  const Icon = !sortable
    ? null
    : !isActive
      ? ChevronsUpDown
      : direction === "asc"
        ? ChevronUp
        : ChevronDown;

  return (
    <th
      className={`relative select-none px-3 py-2 ${alignCls} align-middle whitespace-nowrap border-r border-slate-200 last:border-r-0 ${className}`}
      style={{ width: `${col.width}px`, minWidth: `${col.width}px` }}
      data-testid={testId || `col-header-${col.key}`}
    >
      <div className="flex items-center gap-1.5">
        {sortable ? (
          <button
            type="button"
            className={`inline-flex items-center gap-1 font-medium text-[11px] uppercase tracking-wider transition-colors ${
              isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-800"
            }`}
            onClick={() => onSort?.(nextSort(sort, col.key))}
            title={
              !isActive
                ? `Sort by ${col.label}`
                : direction === "asc"
                  ? `Sort ${col.label} descending`
                  : `Clear sort`
            }
            data-testid={`col-sort-${col.key}`}
          >
            <span>{children ?? col.label}</span>
            {Icon ? <Icon className="h-3 w-3 shrink-0" /> : null}
          </button>
        ) : (
          <span className="font-medium text-[11px] uppercase tracking-wider text-slate-500">
            {children ?? col.label}
          </span>
        )}
      </div>
      {resizable ? (
        <span
          onPointerDown={(e) => onBeginResize?.(col.key, e)}
          data-testid={`col-resize-${col.key}`}
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${col.label} column`}
          title={`Drag to resize ${col.label}`}
          // 4px hit-target so it's easy to grab. Visible on hover as a
          // subtle blue bar so users can find the resize edge before
          // dragging. The border-r on the <th> already gives a permanent
          // divider line so the eye can see where columns split.
          className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-300/70 active:bg-blue-500 transition-colors touch-none"
          style={{ zIndex: 5 }}
        />
      ) : null}
    </th>
  );
}

// -------------------- Client-side sort applier ------------------------

/**
 * Applies a "col:dir" sort string to an array of rows. Comparator is
 * per-column via the columnDefs map ({key: {getSortValue(row)}}). Falls
 * back to the raw property if no getter is defined. Empty sort returns
 * the array unchanged so the underlying order (usually API-side) shines
 * through.
 */
export function applyClientSort(rows, sortStr, columnDefs) {
  if (!sortStr || !Array.isArray(rows) || rows.length === 0) return rows;
  const { column, direction } = parseSort(sortStr);
  if (!column) return rows;
  const def = columnDefs?.[column];
  const getter = def?.getSortValue || ((r) => r[column]);
  const dir = direction === "desc" ? -1 : 1;
  const sorted = [...rows].sort((a, b) => {
    const va = getter(a);
    const vb = getter(b);
    // Nulls / empty sort to the bottom regardless of direction — feels
    // more natural than mixing them with real values.
    const aEmpty = va === null || va === undefined || va === "";
    const bEmpty = vb === null || vb === undefined || vb === "";
    if (aEmpty && !bEmpty) return 1;
    if (!aEmpty && bEmpty) return -1;
    if (aEmpty && bEmpty) return 0;
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
    return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: "base" }) * dir;
  });
  return sorted;
}
